import { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Edit, Trash2, X, DollarSign, Video, Key } from 'lucide-react';
import { useStreams } from '../hooks/useStreams';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import ProfilePicture from '@/components/ProfilePicture';
import { Pagination } from '@/components/Pagination';

export const AdminStreamsTab = () => {
  const {
    streams,
    editingStream,
    streamForm,
    setStreamForm,
    fetchStreams,
    resetStreamForm,
    handleEditStream,
    saveStream,
    deleteStream,
    updateReplay
  } = useStreams();

  const streamFormRef = useRef(null);
  const [contributors, setContributors] = useState([]);
  const [selectedPresenters, setSelectedPresenters] = useState([]);
  const [replayModal, setReplayModal] = useState({ open: false, stream: null });
  const [replayForm, setReplayForm] = useState({
    has_replay: false, replay_url: '', replay_is_free: false,
    replay_thumbnail: '', replay_duration_minutes: ''
  });
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const fetchContributors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contributors/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContributors(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch contributors:', error);
    }
  };

  useEffect(() => {
    fetchStreams();
    fetchContributors();
  }, []);

  useEffect(() => {
    // When editing a stream, load its presenters
    if (editingStream && editingStream.presenter_ids) {
      setSelectedPresenters(editingStream.presenter_ids);
    } else {
      setSelectedPresenters([]);
    }
  }, [editingStream]);

  const togglePresenter = (userId) => {
    setSelectedPresenters(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const onEdit = (stream) => {
    handleEditStream(stream, streamFormRef);
  };

  const onSave = async () => {
    // Include presenter_ids in the form
    const formWithPresenters = { ...streamForm, presenter_ids: selectedPresenters };
    setStreamForm(formWithPresenters);
    await saveStream(fetchStreams, selectedPresenters);
  };

  const onDelete = async (id) => {
    await deleteStream(id, fetchStreams);
  };

  const getPresenterNames = (presenterIds) => {
    if (!presenterIds || presenterIds.length === 0) return null;
    return contributors
      .filter(c => presenterIds.includes(c.user_id))
      .map(c => c.user?.full_name)
      .join(', ');
  };

  const openReplayModal = (stream) => {
    setReplayForm({
      has_replay: stream.has_replay || false,
      replay_url: stream.replay_url || '',
      replay_is_free: stream.replay_is_free || false,
      replay_thumbnail: stream.replay_thumbnail || '',
      replay_duration_minutes: stream.replay_duration_minutes || ''
    });
    setReplayModal({ open: true, stream });
  };

  const uploadReplayVideo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await axios.post(`${API}/upload-video`, body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReplayForm((prev) => ({ ...prev, replay_url: data.file_url, has_replay: true }));
      toast.success('Recording uploaded — save to publish it');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That video could not be uploaded');
    } finally {
      setUploading(false);
    }
  };

  const saveReplayInfo = async () => {
    if (replayModal.stream) {
      await updateReplay(replayModal.stream.id, replayForm, fetchStreams);
      setReplayModal({ open: false, stream: null });
    }
  };

  return (
    <div className="space-y-6">
      <Card ref={streamFormRef}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{editingStream ? 'Edit Stream' : 'Create Live Stream'}</span>
            {editingStream && (
              <Button variant="ghost" size="sm" onClick={resetStreamForm}>
                Cancel Edit
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={streamForm.title}
              onChange={(e) => setStreamForm({ ...streamForm, title: e.target.value })}
              placeholder="Stream Title"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={streamForm.description}
              onChange={(e) => setStreamForm({ ...streamForm, description: e.target.value })}
              placeholder="Stream description"
              rows={3}
            />
          </div>
          <div>
            <Label>Embed URL</Label>
            <Input
              value={streamForm.embed_url}
              onChange={(e) => setStreamForm({ ...streamForm, embed_url: e.target.value })}
              placeholder="YouTube/Zoom embed URL"
            />
          </div>
          <div>
            <Label>Scheduled Date & Time (optional)</Label>
            <Input
              type="datetime-local"
              value={streamForm.scheduled_date}
              onChange={(e) => setStreamForm({ ...streamForm, scheduled_date: e.target.value })}
            />
          </div>
          <div>
            <Label>Timezone</Label>
            <select
              value={streamForm.timezone}
              onChange={(e) => setStreamForm({ ...streamForm, timezone: e.target.value })}
              className="w-full p-2 border rounded-md bg-white text-black"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Anchorage">Alaska Time (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
              <option value="Australia/Sydney">Sydney (AEST)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
          <div>
            <Label>Capacity — max attendees (optional)</Label>
            <Input
              type="number"
              min="0"
              placeholder="Leave blank or 0 for unlimited"
              value={streamForm.capacity ?? ''}
              onChange={(e) => setStreamForm({ ...streamForm, capacity: e.target.value === '' ? null : (parseInt(e.target.value, 10) || 0) })}
              data-testid="stream-capacity-input"
            />
            <p className="text-xs text-gray-500 mt-1">When the cap is reached the event shows “Sold Out” and blocks new registrations/purchases.</p>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={streamForm.is_live}
              onCheckedChange={(checked) => setStreamForm({ ...streamForm, is_live: checked })}
            />
            <Label>Is Live Now</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={streamForm.add_to_calendar}
              onCheckedChange={(checked) => setStreamForm({ ...streamForm, add_to_calendar: checked })}
            />
            <Label>Add to App Calendar</Label>
          </div>
          
          {/* Paid Stream Section */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center space-x-2 mb-4">
              <Switch
                checked={streamForm.is_paid}
                onCheckedChange={(checked) => setStreamForm({ ...streamForm, is_paid: checked })}
              />
              <Label className="font-semibold">Paid Event</Label>
            </div>
            
            {streamForm.is_paid && (
              <div className="space-y-4 pl-4 border-l-2 border-cyan-500">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Live Access Price ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={streamForm.live_price}
                      onChange={(e) => setStreamForm({ ...streamForm, live_price: parseFloat(e.target.value) || 0 })}
                      placeholder="25.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Price for live attendance (includes replay)</p>
                  </div>
                  <div>
                    <Label>Replay-Only Price ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={streamForm.replay_price}
                      onChange={(e) => setStreamForm({ ...streamForm, replay_price: parseFloat(e.target.value) || 0 })}
                      placeholder="15.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Price for replay-only access</p>
                  </div>
                </div>
                <div>
                  <Label>Stream Password</Label>
                  <Input
                    value={streamForm.stream_password}
                    onChange={(e) => setStreamForm({ ...streamForm, stream_password: e.target.value })}
                    placeholder="Password from streaming platform"
                  />
                  <p className="text-xs text-gray-500 mt-1">Password will be sent to purchasers via email and in-app notification</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Presenter Selection */}
          <div>
            <Label>Presenters (for Compensation)</Label>
            <p className="text-xs text-gray-500 mb-2">Select practitioners who will present this stream. They will earn 2 points when the stream goes live.</p>
            
            {/* Selected Presenters */}
            {selectedPresenters.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedPresenters.map(userId => {
                  const contributor = contributors.find(c => c.user_id === userId);
                  return contributor ? (
                    <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                      {contributor.user?.full_name}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-red-500" 
                        onClick={() => togglePresenter(userId)}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            
            {/* Contributor List */}
            <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
              {contributors.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No contributors available</p>
              ) : (
                contributors.map(contributor => (
                  <div 
                    key={contributor.user_id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                      selectedPresenters.includes(contributor.user_id) ? 'bg-cyan-50 border border-cyan-300' : ''
                    }`}
                    onClick={() => togglePresenter(contributor.user_id)}
                  >
                    <input 
                      type="checkbox" 
                      checked={selectedPresenters.includes(contributor.user_id)}
                      onChange={() => {}}
                      className="rounded"
                    />
                    <ProfilePicture src={contributor.user?.profile_picture} size="xs" />
                    <span className="text-sm">{contributor.user?.full_name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <Button onClick={onSave} className="btn-primary w-full">
            {editingStream ? 'Update Stream' : 'Create Stream'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing Streams ({streams.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {streams.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No streams created yet</p>
          ) : (
            <div className="space-y-3">
              {streams.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((stream) => (
                <div key={stream.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      {stream.title}
                      {stream.is_live && <span className="text-xs bg-red-500 text-white px-2 py-1 rounded">LIVE</span>}
                      {stream.is_paid && (
                        <Badge className="bg-green-600">
                          <DollarSign className="w-3 h-3 mr-1" />
                          ${stream.live_price}
                        </Badge>
                      )}
                      {stream.has_replay && (
                        <Badge variant="secondary">
                          <Video className="w-3 h-3 mr-1" />
                          Replay
                        </Badge>
                      )}
                      {stream.capacity ? (
                        stream.is_sold_out ? (
                          <Badge className="bg-red-600" data-testid={`stream-soldout-${stream.id}`}>Sold Out</Badge>
                        ) : (
                          <Badge variant="outline" data-testid={`stream-capacity-${stream.id}`}>
                            {stream.registered_count || 0} / {stream.capacity} registered
                          </Badge>
                        )
                      ) : null}
                    </h3>
                    <p className="text-sm text-gray-600">{stream.description}</p>
                    {stream.scheduled_date && (
                      <p className="text-xs text-gray-500 mt-1">📅 Scheduled: {new Date(stream.scheduled_date).toLocaleString()}</p>
                    )}
                    {getPresenterNames(stream.presenter_ids) && (
                      <p className="text-xs text-cyan-600 mt-1">👤 Presenters: {getPresenterNames(stream.presenter_ids)}</p>
                    )}
                    {stream.is_paid && stream.stream_password && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <Key className="w-3 h-3" />
                        Password: {stream.stream_password}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openReplayModal(stream)}
                            title="Manage the recording"
                            data-testid={`stream-manage-replay-${stream.id}`}>
                      <Video className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onEdit(stream)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Stream?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete &quot;{stream.title}&quot;.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(stream.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination
            page={page}
            totalPages={Math.max(1, Math.ceil(streams.length / PER_PAGE))}
            onChange={setPage}
            testidPrefix="admin-streams-pagination"
            theme="light"
          />
        </CardContent>
      </Card>

      {/* Replay Modal */}
      <Dialog open={replayModal.open} onOpenChange={(open) => !open && setReplayModal({ open: false, stream: null })}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Replay - {replayModal.stream?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={replayForm.has_replay}
                onCheckedChange={(checked) => setReplayForm({ ...replayForm, has_replay: checked })}
                data-testid="replay-available-switch"
              />
              <Label>Replay Available</Label>
            </div>

            {replayForm.has_replay && (
              <>
                <div>
                  <Label>Upload the recording</Label>
                  <Input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                    disabled={uploading}
                    onChange={(e) => uploadReplayVideo(e.target.files?.[0])}
                    data-testid="replay-video-upload"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {uploading
                      ? 'Uploading — keep this window open…'
                      : 'MP4, MOV, WebM or AVI up to 1GB. Use this when there is no provider archive.'}
                  </p>
                </div>

                <div>
                  <Label>Replay URL</Label>
                  <Input
                    value={replayForm.replay_url}
                    onChange={(e) => setReplayForm({ ...replayForm, replay_url: e.target.value })}
                    placeholder="Uploaded file link, or a Vimeo / YouTube embed URL"
                    data-testid="replay-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Everyone with a claim on this recording is notified as soon as you save.
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={replayForm.replay_is_free}
                    onCheckedChange={(checked) => setReplayForm({ ...replayForm, replay_is_free: checked })}
                    data-testid="replay-free-switch"
                  />
                  <Label>Free for everyone (open the archive up on this one)</Label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Thumbnail URL</Label>
                    <Input
                      value={replayForm.replay_thumbnail}
                      onChange={(e) => setReplayForm({ ...replayForm, replay_thumbnail: e.target.value })}
                      placeholder="Optional cover image"
                      data-testid="replay-thumbnail-input"
                    />
                  </div>
                  <div>
                    <Label>Length (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={replayForm.replay_duration_minutes}
                      onChange={(e) => setReplayForm({ ...replayForm, replay_duration_minutes: e.target.value })}
                      placeholder="Optional"
                      data-testid="replay-duration-input"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplayModal({ open: false, stream: null })}>
              Cancel
            </Button>
            <Button onClick={saveReplayInfo} disabled={uploading} data-testid="replay-save-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStreamsTab;
